import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { HeartHandshake, Users, Clock, CheckCircle2, Phone, Mail, Calendar } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUS_STYLES = {
  new: "bg-blue-100 text-blue-800 border-blue-200",
  contacted: "bg-amber-100 text-amber-800 border-amber-200",
  approved: "bg-green-100 text-green-800 border-green-200",
  declined: "bg-red-100 text-red-800 border-red-200",
};

const Applications = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");

  const fetchApps = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const res = await axios.get(`${API}/applications${params}`);
      setApps(res.data.items || []);
    } catch {
      toast.error("Failed to load applications");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchApps(); }, [statusFilter]);

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/applications/${id}/status`, { status });
      toast.success("Status updated");
      fetchApps();
      if (selected?.id === id) setSelected({ ...selected, status });
    } catch {
      toast.error("Failed to update status");
    }
  };

  const counts = {
    total: apps.length,
    new: apps.filter((a) => a.status === "new").length,
    approved: apps.filter((a) => a.status === "approved").length,
  };

  return (
    <div className="space-y-6" data-testid="applications-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Assistance Applications
          </h1>
          <p className="text-slate-500 mt-1">Review requests submitted by individuals seeking clothing help</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="declined">Declined</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          { label: "Total Applications", value: counts.total, icon: Users, color: "text-blue-600", bg: "bg-blue-100" },
          { label: "New / Unreviewed", value: counts.new, icon: Clock, color: "text-amber-600", bg: "bg-amber-100" },
          { label: "Approved", value: counts.approved, icon: CheckCircle2, color: "text-green-600", bg: "bg-green-100" },
        ].map((s) => (
          <Card key={s.label} className="dashboard-widget stat-card">
            <CardContent className="p-6 flex items-center gap-4">
              <div className={`w-12 h-12 ${s.bg} rounded-xl flex items-center justify-center`}>
                <s.icon className={`w-6 h-6 ${s.color}`} />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-900">{s.value}</div>
                <div className="text-sm text-slate-500">{s.label}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><HeartHandshake className="w-5 h-5 text-amber-600" />Applications</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-slate-500 py-8 text-center">Loading...</p>
          ) : apps.length === 0 ? (
            <p className="text-slate-500 py-8 text-center" data-testid="no-applications">No applications yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Needs</TableHead>
                  <TableHead>Household</TableHead>
                  <TableHead>Employment</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {apps.map((a) => (
                  <TableRow key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => setSelected(a)} data-testid={`application-row-${a.id}`}>
                    <TableCell>
                      <div className="font-medium text-slate-900">{a.first_name} {a.last_name}</div>
                      <div className="text-xs text-slate-500">{a.city}, {a.state}</div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-600 max-w-[220px]">{(a.assistance_type || []).join(", ") || "—"}</TableCell>
                    <TableCell className="text-sm text-slate-600">{a.household_size} · {a.household_income}</TableCell>
                    <TableCell className="text-sm text-slate-600">{a.employment_status}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={STATUS_STYLES[a.status] || STATUS_STYLES.new}>{a.status}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="application-detail">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>{selected.first_name} {selected.last_name}</DialogTitle>
                <DialogDescription>Assistance application details and screening responses.</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 text-sm">
                <div className="flex flex-col gap-1 text-slate-600">
                  <span className="flex items-center gap-2"><Mail className="w-4 h-4" />{selected.email}</span>
                  <span className="flex items-center gap-2"><Phone className="w-4 h-4" />{selected.phone}</span>
                  <span className="text-slate-500">{selected.city}, {selected.state} {selected.zip_code}</span>
                </div>
                <Detail label="Type of help" value={(selected.assistance_type || []).join(", ") || "—"} />
                <Detail label="Household size" value={selected.household_size} />
                <Detail label="Household income" value={selected.household_income} />
                <Detail label="Employment" value={`${selected.employment_status}${selected.employment_details ? " — " + selected.employment_details : ""}`} />
                <Detail label="Government assistance" value={(selected.government_assistance || []).join(", ") || "None reported"} />
                {selected.government_assistance_details && <Detail label="Assistance details" value={selected.government_assistance_details} />}
                <Detail label="Available days" value={(selected.contact_days || []).join(", ") || "—"} icon={Calendar} />
                <Detail label="Available times" value={(selected.contact_times || []).join(", ") || "—"} />
                {selected.notes && <Detail label="Notes" value={selected.notes} />}
                <div className="pt-2">
                  <label className="text-xs text-slate-500 uppercase tracking-wider">Update Status</label>
                  <Select value={selected.status} onValueChange={(v) => updateStatus(selected.id, v)}>
                    <SelectTrigger className="mt-1" data-testid="update-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="declined">Declined</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Detail = ({ label, value }) => (
  <div>
    <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
    <div className="text-slate-800">{value}</div>
  </div>
);

export default Applications;
